// ---------- Catálogo de campos da etiqueta (Sprint 34) ----------
//
// Sprint 34 substituiu o modelo de "blocos" flexíveis (TemplateBlock / Slot /
// SlotContent) por uma **lista de campos com checkbox**: o modelo só persiste
// QUAIS campos estão ligados (`LabelTemplate.includedFields: FieldSourceKind[]`).
// A ORDEM de impressão, o CAPTION e o ESTILO de cada campo são catálogo no
// código (este arquivo), nunca configuração do usuário. A renderização sempre
// percorre `TEMPLATE_FIELD_KINDS` nesta ordem, emite uma linha por campo ligado,
// e termina com o **QR + a logo do EtiquetaChef** fixos no rodapé (code-driven,
// fora de `includedFields` — ver `project_label_qr_logo_always`).
//
// Espelho Kotlin manual em `apps/android/.../core/printing/FieldCatalog.kt`
// (mesma duplicação intencional dos enums/schemas — ver CLAUDE.md).

import { BlockAlign, FieldSourceKind, TextScale } from './enums';

/**
 * Como um campo é renderizado na etiqueta:
 * - `HEADER`: linha centralizada, sem rótulo (ex.: nome do produto).
 * - `KEY_VALUE`: "Caption: valor" justificado (rótulo à esquerda, valor à direita).
 * - `FULL_TEXT`: texto de largura cheia com quebra; caption opcional como prefixo.
 */
export type FieldRenderStyle = 'HEADER' | 'KEY_VALUE' | 'FULL_TEXT';

/** Estilo automático de um campo do catálogo. Sem configuração por elemento. */
export type FieldStyle = {
  readonly render: FieldRenderStyle;
  /**
   * Sufixo da chave i18n do caption (web: `labelTemplates.fieldCaption.<captionKey>`;
   * Android: `template_field_caption_<captionKey>`). `null` em `HEADER` (sem rótulo)
   * e em `FULL_TEXT` sem prefixo.
   */
  readonly captionKey: string | null;
  readonly scale: TextScale;
  readonly bold: boolean;
  /** Texto branco em fundo preto (faixa de destaque). Térmica só pinta preto. */
  readonly inverse: boolean;
  readonly align: BlockAlign;
  /** Vem ligado por padrão num modelo novo. */
  readonly defaultOn: boolean;
};

/**
 * Ordem canônica de impressão dos campos. Fonte única da ordem — a renderização
 * (preview web + `LabelSpecBuilder` Android) percorre esta lista. Também é o
 * conjunto de origens permitidas em `includedFields` (`TemplateFieldKindSchema`).
 *
 * Origens descartadas (não toggláveis): `TEXTO_LIVRE` (exigia valor literal),
 * `EMPTY` (placeholder), `ORIGIN` (duplicava `PREPARO`), `RULE_LABEL`/`DURATION`
 * (detalhe de regra — cobertos por `VALIDADE`/`TIPO_ETIQUETA`).
 */
export const TEMPLATE_FIELD_KINDS = [
  FieldSourceKind.PRODUCT_NAME,
  FieldSourceKind.PREPARO,
  FieldSourceKind.VALIDADE,
  FieldSourceKind.LOTE,
  FieldSourceKind.UNIT_QUANTITY,
  FieldSourceKind.STORAGE,
  FieldSourceKind.SKU,
  FieldSourceKind.FORNECEDOR,
  FieldSourceKind.INGREDIENTES,
  FieldSourceKind.ALERGENICOS,
  FieldSourceKind.DESCRIPTION,
  // Bloco de metadados no rodapé do corpo, sob uma única régua e juntos:
  // Responsável + Estabelecimento (nesta ordem).
  FieldSourceKind.RESPONSAVEL,
  FieldSourceKind.TENANT_NAME,
  FieldSourceKind.PRINTED_AT,
] as const;
export type TemplateFieldKind = (typeof TEMPLATE_FIELD_KINDS)[number];

/** Estilo automático por campo. Ordem vem de `TEMPLATE_FIELD_KINDS`, não daqui. */
export const FIELD_CATALOG: Readonly<Record<TemplateFieldKind, FieldStyle>> = {
  [FieldSourceKind.PRODUCT_NAME]: { render: 'HEADER', captionKey: null, scale: TextScale.LARGE, bold: true, inverse: false, align: BlockAlign.CENTER, defaultOn: true },
  [FieldSourceKind.PREPARO]: { render: 'KEY_VALUE', captionKey: 'preparo', scale: TextScale.NORMAL, bold: false, inverse: false, align: BlockAlign.LEFT, defaultOn: true },
  // Validade: mesma fonte dos demais campos (NORMAL), sempre negrito e em
  // destaque inverso (fundo preto, fonte branca).
  [FieldSourceKind.VALIDADE]: { render: 'KEY_VALUE', captionKey: 'validade', scale: TextScale.NORMAL, bold: true, inverse: true, align: BlockAlign.LEFT, defaultOn: true },
  [FieldSourceKind.LOTE]: { render: 'KEY_VALUE', captionKey: 'lote', scale: TextScale.NORMAL, bold: false, inverse: false, align: BlockAlign.LEFT, defaultOn: true },
  [FieldSourceKind.UNIT_QUANTITY]: { render: 'KEY_VALUE', captionKey: 'unitQuantity', scale: TextScale.NORMAL, bold: false, inverse: false, align: BlockAlign.LEFT, defaultOn: false },
  [FieldSourceKind.STORAGE]: { render: 'KEY_VALUE', captionKey: 'storage', scale: TextScale.NORMAL, bold: false, inverse: false, align: BlockAlign.LEFT, defaultOn: false },
  [FieldSourceKind.SKU]: { render: 'KEY_VALUE', captionKey: 'sku', scale: TextScale.SMALL, bold: false, inverse: false, align: BlockAlign.LEFT, defaultOn: false },
  // Fornecedor: snapshot dos nomes na própria etiqueta (`Label.supplierNames`),
  // compostos numa única string por `composeSupplierField`. FULL_TEXT porque com
  // 2+ fornecedores a linha estoura a largura — KEY_VALUE justifica em UMA linha
  // e cortaria o texto. COM caption (ao contrário de TenantName): nome de empresa
  // sem rótulo se confunde com o próprio estabelecimento.
  [FieldSourceKind.FORNECEDOR]: { render: 'FULL_TEXT', captionKey: 'fornecedor', scale: TextScale.SMALL, bold: false, inverse: false, align: BlockAlign.LEFT, defaultOn: false },
  [FieldSourceKind.INGREDIENTES]: { render: 'FULL_TEXT', captionKey: 'ingredientes', scale: TextScale.SMALL, bold: false, inverse: false, align: BlockAlign.LEFT, defaultOn: false },
  [FieldSourceKind.ALERGENICOS]: { render: 'FULL_TEXT', captionKey: 'alergenicos', scale: TextScale.SMALL, bold: true, inverse: false, align: BlockAlign.LEFT, defaultOn: false },
  [FieldSourceKind.DESCRIPTION]: { render: 'FULL_TEXT', captionKey: null, scale: TextScale.SMALL, bold: false, inverse: false, align: BlockAlign.LEFT, defaultOn: false },
  // Responsável + Estabelecimento = bloco de metadados no rodapé do corpo, sob
  // uma única régua e juntos (nesta ordem). Ver `TEMPLATE_FIELD_KINDS`.
  [FieldSourceKind.RESPONSAVEL]: { render: 'KEY_VALUE', captionKey: 'responsavel', scale: TextScale.NORMAL, bold: false, inverse: false, align: BlockAlign.LEFT, defaultOn: true },
  // Estabelecimento: bloco composto (nome · endereço · CNPJ/P.IVA) numa ÚNICA
  // string, resolvido pelo renderer (`composeTenantField`). FULL_TEXT para
  // quebrar a linha longa dentro das margens; alinhado à esquerda.
  [FieldSourceKind.TENANT_NAME]: { render: 'FULL_TEXT', captionKey: null, scale: TextScale.SMALL, bold: false, inverse: false, align: BlockAlign.LEFT, defaultOn: false },
  [FieldSourceKind.PRINTED_AT]: { render: 'KEY_VALUE', captionKey: 'printedAt', scale: TextScale.XSMALL, bold: false, inverse: false, align: BlockAlign.LEFT, defaultOn: false },
};

/** Conjunto de origens válidas em `includedFields` (lookup O(1)). */
export const TEMPLATE_FIELD_KIND_SET: ReadonlySet<string> = new Set(TEMPLATE_FIELD_KINDS);

/** Campos ligados por padrão num modelo novo (na ordem canônica). */
export const DEFAULT_INCLUDED_FIELDS: readonly TemplateFieldKind[] =
  TEMPLATE_FIELD_KINDS.filter((kind) => FIELD_CATALOG[kind].defaultOn);

/**
 * Filtra/ordena uma lista de campos ligados pela ordem canônica do catálogo,
 * descartando kinds desconhecidos (defesa contra docs legados/corrompidos).
 * Usado pela renderização (preview + builder).
 */
export function orderedIncludedFields(included: readonly FieldSourceKind[]): TemplateFieldKind[] {
  const set = new Set<string>(included);
  return TEMPLATE_FIELD_KINDS.filter((kind) => set.has(kind));
}

/** Separador entre as partes do campo Estabelecimento. Espelhado em Kotlin. */
export const TENANT_FIELD_SEPARATOR = ' · ';

/**
 * Monta a string única do campo Estabelecimento a partir das partes já
 * resolvidas (nome, endereço, identificação fiscal com rótulo), na ordem
 * canônica — a identificação fiscal (CNPJ/P.IVA/...) vai no fim. Partes
 * vazias/ausentes são omitidas. Tudo vazio → "". O rótulo fiscal vem de fora
 * (i18n por plataforma). Espelhado em Kotlin (`LabelSpecBuilder.composeTenantField`).
 */
export function composeTenantField(parts: {
  name?: string | undefined;
  address?: string | undefined;
  taxLabeled?: string | undefined;
}): string {
  return [parts.name, parts.address, parts.taxLabeled]
    .map((p) => p?.trim() ?? '')
    .filter((p) => p !== '')
    .join(TENANT_FIELD_SEPARATOR);
}

/** Separador entre nomes de fornecedores. Espelhado em Kotlin. */
export const SUPPLIER_FIELD_SEPARATOR = ', ';

/**
 * Monta a string única do campo Fornecedor a partir do snapshot de nomes da
 * etiqueta (`Label.supplierNames`), na ordem em que foram gravados (a ordem de
 * `Product.supplierIds`). Nomes vazios/só-espaço são omitidos; lista vazia ou
 * ausente devolve "" — e o renderer omite a linha inteira, sem caption órfão.
 * Sem deduplicação: dois fornecedores homônimos são dois fornecedores.
 * Espelhado em Kotlin (`FieldCatalog.kt: composeSupplierField`).
 */
export function composeSupplierField(names: readonly string[] | undefined): string {
  return (names ?? [])
    .map((n) => n.trim())
    .filter((n) => n !== '')
    .join(SUPPLIER_FIELD_SEPARATOR);
}

const FEW_FIELDS: readonly TemplateFieldKind[] = [
  FieldSourceKind.PRODUCT_NAME,
  FieldSourceKind.VALIDADE,
];
const MEDIUM_FIELDS: readonly TemplateFieldKind[] = [
  FieldSourceKind.PRODUCT_NAME,
  FieldSourceKind.PREPARO,
  FieldSourceKind.VALIDADE,
  FieldSourceKind.LOTE,
  FieldSourceKind.RESPONSAVEL,
];
const MANY_FIELDS: readonly TemplateFieldKind[] = [
  FieldSourceKind.PRODUCT_NAME,
  FieldSourceKind.PREPARO,
  FieldSourceKind.VALIDADE,
  FieldSourceKind.LOTE,
  FieldSourceKind.RESPONSAVEL,
  FieldSourceKind.UNIT_QUANTITY,
  FieldSourceKind.STORAGE,
  FieldSourceKind.INGREDIENTES,
  FieldSourceKind.ALERGENICOS,
];

/**
 * Sprint 34: sugere os campos de um modelo NOVO a partir do tamanho da
 * etiqueta (área em mm²). Etiqueta pequena cabe poucos campos; grande cabe
 * mais. Usado pelo editor em modo CREATE: ao informar largura/altura, os
 * campos são gerados automaticamente (decisão do owner 2026-05-21). Os
 * thresholds batem com o reseed dos defaults (51×25 ≈ poucos, 57×51 ≈ médio,
 * 102×152 ≈ muitos).
 */
export function suggestIncludedFieldsBySize(widthMm: number, heightMm: number): TemplateFieldKind[] {
  const area = Math.max(0, widthMm) * Math.max(0, heightMm);
  const chosen = area <= 2000 ? FEW_FIELDS : area <= 4000 ? MEDIUM_FIELDS : MANY_FIELDS;
  return [...chosen];
}
